/**
 * The game clock and the distance unit of the whole universe — the ONE place that owns them.
 *
 * - Distance: 1 map unit = 1 light year (ly). `star_lanes.distance` is in ly.
 * - Time: everything is expressed in real hours at GAME_SPEED 1. The only runtime knob is the
 *   GAME_SPEED multiplier (dev servers, economy simulation, E2E tests); production runs at 1.
 *
 * Values are fixed in code on purpose (no per-duration environment variables). Tune them here;
 * docs/game-clock.md explains the reasoning.
 */

export const HOUR_MS = 3_600_000;
export const MINUTE_MS = 60_000;
export const DAY_MS = 24 * HOUR_MS;

// ---- Speeds (ly/h) ----

export const SHIP_SPEED_LY_PER_H: Record<string, number> = {
  scout: 15,         // survey ship
  corvette: 12,
  frigate: 9,
  construction: 8,   // builder / outpost ship
  cruiser: 6,
};

/** Used for an unknown ship class so nothing can ever get a zero speed. */
export const FALLBACK_SPEED_LY_PER_H = 6;

export function shipSpeedLyPerHour(shipClass: string): number {
  return SHIP_SPEED_LY_PER_H[shipClass] ?? FALLBACK_SPEED_LY_PER_H;
}

/** A fleet moves at the speed of its slowest ship, plus the civilization's tech bonus (ly/h). */
export function fleetSpeedLyPerHour(shipClasses: Iterable<string>, bonusLyPerHour = 0): number {
  let slowest = Infinity;
  for (const c of shipClasses) slowest = Math.min(slowest, shipSpeedLyPerHour(c));
  if (!Number.isFinite(slowest)) slowest = FALLBACK_SPEED_LY_PER_H;
  return slowest + Math.max(0, bonusLyPerHour);
}

// ---- Durations (real hours at GAME_SPEED 1) ----

export const GAME_HOURS = {
  /** Survey: base + per planet, capped. See `surveyDurationH`. */
  surveyBase: 0.5,
  surveyPerPlanet: 0.25,
  surveyMax: 2,
  outpost: 2,
  miningStation: 3,
  energyStation: 2,
  /** Track 7: logistics hub (depot) and builder ship. */
  logisticsHub: 6,
  builder: 3,
  scout: 2,
  corvette: 1,
  frigate: 3,
  cruiser: 8,
  /** Station upgrade: legacy per-step time; the per-tier times live in `STATION_TIERS` (shared/mining.ts). */
  stationUpgradeStep: 2,
  /** Occupation of a captured system (doubled against a long-offline defender). */
  occupation: 2,
  /** Notice period of a cancelled pact/alliance. */
  treatyNotice: 6,
  /** Real time between two decisions of one NPC civilization. */
  npcTurn: 1,
  /** Research time by tech tier. */
  researchByTier: { 1: 1, 2: 4, 3: 10, 4: 24 } as Record<number, number>,
} as const;

export function surveyDurationH(planetCount: number): number {
  return Math.min(GAME_HOURS.surveyMax, GAME_HOURS.surveyBase + GAME_HOURS.surveyPerPlanet * Math.max(0, planetCount));
}

export function researchDurationH(tier: number): number {
  return GAME_HOURS.researchByTier[tier] ?? GAME_HOURS.researchByTier[4]!;
}

export const hoursToMs = (hours: number): number => Math.round(hours * HOUR_MS);

// ---- GAME_SPEED ----

/** Parses the GAME_SPEED setting: a positive finite number, otherwise 1. */
export function parseGameSpeed(raw: string | undefined | null): number {
  const n = raw === undefined || raw === null || raw.trim() === '' ? 1 : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/** Real milliseconds a game duration takes at the given GAME_SPEED (never below 1 ms). */
export function scaleDurationMs(ms: number, gameSpeed = 1): number {
  return Math.max(1, Math.round(ms / gameSpeed));
}

// ---- Travel ----

/**
 * Real milliseconds a jump of `distanceLy` takes at `speedLyPerHour` (bonus already included).
 * The server computes this once at departure and stores the arrival time.
 */
export function travelDurationMs(distanceLy: number, speedLyPerHour: number, gameSpeed = 1): number {
  const speed = speedLyPerHour > 0 ? speedLyPerHour : FALLBACK_SPEED_LY_PER_H;
  return scaleDurationMs((Math.max(0, distanceLy) / speed) * HOUR_MS, gameSpeed);
}

// ---- Formatting (Hungarian UI) ----

const nf = (n: number, digits: number): string => {
  const r = Number(n.toFixed(digits));
  return String(r).replace('.', ',');
};

export function formatLy(distanceLy: number): string {
  return `${nf(distanceLy, distanceLy < 10 ? 1 : 0)} ly`;
}

export function formatSpeed(lyPerHour: number): string {
  return `${nf(lyPerHour, 1)} ly/h`;
}

/**
 * Human duration, two units at most: "45 mp", "12 p", "5 ó 30 p", "2 n 4 ó".
 * `ms` is the REAL remaining time (already GAME_SPEED-scaled), shown as it is.
 */
export function formatGameDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return h > 0 ? `${d} n ${h} ó` : `${d} n`;
  if (h > 0) return m > 0 ? `${h} ó ${m} p` : `${h} ó`;
  if (m > 0) return s > 0 && m < 10 ? `${m} p ${s} mp` : `${m} p`;
  return `${s} mp`;
}

/** "5 ó 30 p alatt, érkezés 14:20" — duration plus the clock time (and the day when it is not today). */
export function formatEta(arrivesAt: Date | string | number, now: Date | number = Date.now()): string {
  const at = new Date(arrivesAt);
  const nowMs = typeof now === 'number' ? now : now.getTime();
  const hhmm = at.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
  const sameDay = at.toDateString() === new Date(nowMs).toDateString();
  const when = sameDay ? hhmm : `${at.toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' })} ${hhmm}`;
  return `${formatGameDuration(at.getTime() - nowMs)} · ${when}`;
}
